import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, TextField, Typography } from '@material-ui/core'
import NewProjectModal from './ProjectList/NewProjectModal'
import ScopeSelectionModal from './ProjectList/ScopeSelectionModal'
import ContextIndicator from './shared/ContextIndicator'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'estimating', label: 'Estimating' },
  { key: 'review', label: 'Review' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'won_lost', label: 'Won/Lost' }
]

const STATUS_PILL = {
  draft:      { label: 'Draft',      className: 'pill-neutral' },
  estimating: { label: 'Estimating', className: 'pill-info' },
  review:     { label: 'Review',     className: 'pill-accent' },
  submitted:  { label: 'Submitted',  className: 'pill-brand' },
  won:        { label: 'Won',        className: 'pill-ok' },
  lost:       { label: 'Lost',       className: 'pill-risk' }
}

// Pipeline stage dots — 6 segments showing progress
const STAGES = ['Intake', 'Takeoff', 'Services', 'Pricing', 'Review', 'Submit']

const stageName = (status) => {
  const map = { draft: 'Intake', estimating: 'Services', review: 'Review', submitted: 'Submit', won: 'Submit', lost: 'Submit' }
  return map[status] || 'Intake'
}

const stageIndex = (status) => STAGES.indexOf(stageName(status))

const StageDots = ({ status }) => {
  const idx = stageIndex(status)
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {STAGES.map((_, i) => (
        <div key={i} style={{
          width: 10, height: 4, borderRadius: 2,
          background: i <= idx ? 'var(--brand, var(--primary))' : 'var(--bg-sunken, #e0e0e0)'
        }} />
      ))}
    </div>
  )
}

const daysUntil = (dateStr) => {
  if (!dateStr) return null
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000)
  return diff
}

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="2" y1="4" x2="14" y2="4" /><line x1="2" y1="8" x2="14" y2="8" /><line x1="2" y1="12" x2="14" y2="12" />
  </svg>
)

const GridIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="5" height="5" rx="1" /><rect x="9" y="2" width="5" height="5" rx="1" />
    <rect x="2" y="9" width="5" height="5" rx="1" /><rect x="9" y="9" width="5" height="5" rx="1" />
  </svg>
)

const BoardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="2" width="4" height="12" rx="1" /><rect x="6" y="2" width="4" height="8" rx="1" /><rect x="11" y="2" width="4" height="10" rx="1" />
  </svg>
)

const FlagIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
  </svg>
)

const marginColor = (margin) => {
  if (margin === null) return 'var(--ink-4)'
  if (margin >= 20) return 'var(--ok)'
  if (margin >= 10) return 'var(--warn)'
  return 'var(--risk)'
}

const calcMargin = (p) => p.totalSell && p.totalCost
  ? ((p.totalSell - p.totalCost) / p.totalSell) * 100
  : null

const calcCovPct = (p) => p.totalItems ? Math.round(((p.coveredItems || 0) / p.totalItems) * 100) : 0

// ─── Grid Card View ───
const ProjectCard = ({ project, onClick, ctx }) => {
  const sp = STATUS_PILL[project.status] || STATUS_PILL.draft
  const days = daysUntil(project.bidDueDate)
  const margin = calcMargin(project)
  const covPct = calcCovPct(project)
  const bidVal = project.totalSell || project.totalCost

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-panel)', border: '1px solid var(--line-1, var(--line))',
        borderRadius: 12, padding: 16, cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 10,
        transition: 'box-shadow 0.12s'
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(30,40,35,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Header: bid number + status pill */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.02em' }}>
          {project.bidNumber || '\u2014'}
        </span>
        <span className={`pill ${sp.className}`}><span className="dot" />{sp.label}</span>
      </div>

      {/* Title + client + context indicators */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', lineHeight: 1.3 }}>{project.name}</span>
          <ContextIndicator
            hasBlocker={ctx?.hasOutputBlocker}
            noteCount={0}
            hasReview={ctx?.reviewsUpcoming > 0}
            tasksOverdue={ctx?.tasksOverdue}
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
          {[project.client, project.location].filter(Boolean).join(' \u00b7 ')}
        </div>
      </div>

      {/* Stage dots + name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <StageDots status={project.status} />
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{stageName(project.status)}</span>
      </div>

      {/* Metrics row: BID / MARGIN / COVERAGE */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 4 }}>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', fontWeight: 600 }}>Bid</div>
          <div style={{ fontFamily: 'var(--f-display, serif)', fontWeight: 600, fontSize: 15, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
            {bidVal ? fmt(bidVal) : '\u2014'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', fontWeight: 600 }}>Margin</div>
          <div style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 13, fontWeight: 600, color: marginColor(margin), fontVariantNumeric: 'tabular-nums' }}>
            {margin !== null ? margin.toFixed(1) + '%' : '\u2014'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', fontWeight: 600 }}>Coverage</div>
          <div style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 13, fontWeight: 600, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
            {covPct}%
          </div>
        </div>
      </div>

      {/* Footer: date + days pill */}
      {project.bidDueDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          <span style={{ color: 'var(--ink-3)', display: 'flex' }}><FlagIcon /></span>
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            {new Date(project.bidDueDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
          </span>
          {days !== null && (
            <span className={`pill ${days <= 0 ? 'pill-risk' : days <= 7 ? 'pill-warn' : 'pill-neutral'}`} style={{ fontSize: 9 }}>
              <span className="dot" />{days <= 0 ? 'Overdue' : `${days}d left`}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const GridView = ({ projects, onNavigate, onNewProject, contextCounts }) => {
  if (projects.length === 0) {
    return (
      <div onClick={onNewProject} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', cursor: 'pointer' }}>
        No projects yet — click here to create one.
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
      {projects.map(p => (
        <ProjectCard key={p._id} project={p} onClick={() => onNavigate(p._id)} ctx={contextCounts[p._id]} />
      ))}
    </div>
  )
}

// ─── Board / Pipeline View ───
const BOARD_STAGES = STAGES // Intake, Takeoff, Services, Pricing, Review, Submit

const statusToStage = (status) => {
  const map = { draft: 'Intake', estimating: 'Services', review: 'Review', submitted: 'Submit', won: 'Submit', lost: 'Submit' }
  return map[status] || 'Intake'
}

const BoardView = ({ projects, onNavigate, contextCounts }) => {
  const columns = useMemo(() => {
    const cols = BOARD_STAGES.map(stage => ({
      stage,
      projects: [],
      total: 0
    }))
    projects.forEach(p => {
      const target = statusToStage(p.status)
      const col = cols.find(c => c.stage === target)
      if (col) {
        col.projects.push(p)
        col.total += (p.totalSell || p.totalCost || 0)
      }
    })
    return cols
  }, [projects])

  return (
    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', minHeight: 400, alignItems: 'flex-start' }}>
      {columns.map(col => (
        <div key={col.stage} style={{ minWidth: 220, width: 220, flexShrink: 0 }}>
          {/* Column header */}
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 6, padding: '10px 8px 8px',
            borderBottom: '2px solid var(--line-2)', marginBottom: 8
          }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{col.stage}</span>
            <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11, color: 'var(--ink-4)' }}>{col.projects.length}</span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 11, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>
              {col.total > 0 ? fmt(col.total) : ''}
            </span>
          </div>
          {/* Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {col.projects.map(p => {
              const days = daysUntil(p.bidDueDate)
              const bidVal = p.totalSell || p.totalCost
              return (
                <div
                  key={p._id}
                  onClick={() => onNavigate(p._id)}
                  style={{
                    background: 'var(--bg-panel)', border: '1px solid var(--line-1, var(--line))',
                    borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', gap: 6,
                    transition: 'box-shadow 0.12s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,40,35,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', lineHeight: 1.3 }}>{p.name}</span>
                    <ContextIndicator
                      hasBlocker={contextCounts[p._id]?.hasOutputBlocker}
                      noteCount={0}
                      hasReview={contextCounts[p._id]?.reviewsUpcoming > 0}
                      tasksOverdue={contextCounts[p._id]?.tasksOverdue}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {[p.client, p.location].filter(Boolean).join(' \u00b7 ') || '\u00a0'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
                    <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12, fontWeight: 600, color: 'var(--ink)', fontVariantNumeric: 'tabular-nums' }}>
                      {bidVal ? fmt(bidVal) : '\u2014'}
                    </span>
                    {days !== null && (
                      <span className={`pill ${days <= 0 ? 'pill-risk' : days <= 7 ? 'pill-warn' : 'pill-neutral'}`} style={{ fontSize: 9 }}>
                        <span className="dot" />{days <= 0 ? 'Overdue' : `${days}d left`}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
            {col.projects.length === 0 && (
              <div style={{ padding: '20px 8px', textAlign: 'center', fontSize: 12, color: 'var(--ink-4)' }}>
                No projects
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const VIEW_MODES = [
  { key: 'table', icon: <TableIcon /> },
  { key: 'grid', icon: <GridIcon /> },
  { key: 'board', icon: <BoardIcon /> }
]

const RowActionMenu = ({ project, onClone, onDelete, cloning }) => {
  const [open, setOpen] = useState(false)
  const ref = React.useRef(null)

  useEffect(() => {
    if (!open) return
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="hmi-btn hmi-btn-ghost hmi-btn-sm"
        style={{ width: 28, height: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--ink-4)' }}
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
      >
        ⋯
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4,
          background: 'var(--bg-panel)', border: '1px solid var(--line-1, var(--line))',
          borderRadius: 8, boxShadow: '0 4px 12px rgba(30,40,35,0.12)',
          zIndex: 100, minWidth: 150, overflow: 'hidden'
        }}>
          <button
            style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 12px', border: 0, background: 'transparent', color: 'var(--ink)', fontSize: 12.5, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-panel-alt)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={(e) => { e.stopPropagation(); setOpen(false); onClone(e, project) }}
            disabled={cloning === project._id}
          >
            {cloning === project._id ? 'Cloning…' : 'Duplicate'}
          </button>
          <button
            style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 12px', border: 0, background: 'transparent', color: 'var(--risk)', fontSize: 12.5, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--risk-tint)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(project) }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  )
}

const BidEstimationProjectsDashboard = (props) => {
  const history = useHistory()
  const { url } = props
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [viewMode, setViewMode] = useState('table')
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [scopeModalProject, setScopeModalProject] = useState(null)
  const [cloning, setCloning] = useState(null)
  const [contextCounts, setContextCounts] = useState({})
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingProject, setDeletingProject] = useState(false)

  useEffect(() => { fetchProjects() }, [])

  const fetchProjects = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects`).then(res => {
      setProjects(res.data)
      setLoading(false)
    }).catch(err => { console.error(err); setLoading(false) })
  }

  useEffect(() => {
    if (!projects?.length) return
    const projectIds = projects.map(p => p._id)
    axios.post(`${url}/api/bid-estimation/projects/context/batch-summary`, { projectIds })
      .then(res => setContextCounts(res.data))
      .catch(() => {})
  }, [projects])

  const handleClone = (e, project) => {
    e.stopPropagation()
    setCloning(project._id)
    axios.post(`${url}/api/bid-estimation/projects/${project._id}/clone`).then(res => {
      setCloning(null)
      history.push(`/bid-estimation/${res.data._id}`)
    }).catch(() => setCloning(null))
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    if (deleteConfirmText !== deleteTarget.name) return
    setDeletingProject(true)
    axios.delete(`${url}/api/bid-estimation/projects/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      setDeleteConfirmText('')
      fetchProjects()
    }).catch(() => {
      setDeleteTarget(null)
      setDeleteConfirmText('')
    }).finally(() => setDeletingProject(false))
  }

  const handleSave = (created) => {
    setModalOpen(false)
    fetchProjects()
    if (created) setScopeModalProject(created)
  }

  // Filters
  const filtered = useMemo(() => {
    let list = projects
    if (statusFilter === 'won_lost') list = list.filter(p => p.status === 'won' || p.status === 'lost')
    else if (statusFilter !== 'all') list = list.filter(p => p.status === statusFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.client || '').toLowerCase().includes(q) ||
        (p.bidNumber || '').toLowerCase().includes(q)
      )
    }
    return list.sort((a, b) => {
      const da = a.bidDueDate ? new Date(a.bidDueDate) : new Date('2099-01-01')
      const db = b.bidDueDate ? new Date(b.bidDueDate) : new Date('2099-01-01')
      return da - db
    })
  }, [projects, statusFilter, search])

  // Stats
  const stats = useMemo(() => {
    const active = projects.filter(p => !['won', 'lost'].includes(p.status))
    const submitted = projects.filter(p => p.status === 'submitted')
    const dueThisWeek = projects.filter(p => {
      const d = daysUntil(p.bidDueDate)
      return d !== null && d >= 0 && d <= 7
    })
    const won = projects.filter(p => p.status === 'won').length
    const lost = projects.filter(p => p.status === 'lost').length
    const totalPipeline = active.reduce((s, p) => s + (p.totalSell || p.totalCost || 0), 0)
    const outForAward = submitted.reduce((s, p) => s + (p.totalSell || p.totalCost || 0), 0)
    return {
      activePipeline: totalPipeline,
      activeCount: active.length,
      outForAward,
      submittedCount: submitted.length,
      dueThisWeek: dueThisWeek.length,
      winRate: won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0,
      wonCount: won,
      lostCount: lost
    }
  }, [projects])

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      {/* Page header */}
      <div className="page-head">
        <div className="title-row">
          <h1>Project Bids/Estimates</h1>
          {/* <span className="meta">H.M.I Estimating · {new Date().getFullYear()} portfolio</span> */}
          <div className="actions">
            <button className="hmi-btn hmi-btn-ghost" onClick={() => window.location.replace('/')}>
              ← Exit to Portal
            </button>
          </div>
        </div>
      </div>

      <div className="hmi-body">
        {/* Search + Filter tabs + View toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative', width: 260 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-4)', display: 'flex', pointerEvents: 'none' }}>
              <SearchIcon />
            </span>
            <input
              type="text"
              placeholder="Search projects, codes, clients\u2026"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', height: 34, padding: '0 12px 0 30px', borderRadius: 8,
                border: '1px solid var(--line-2, var(--line))',
                background: 'var(--bg-panel-alt, #f5f5f5)',
                fontSize: 13, outline: 'none', fontFamily: 'var(--f-sans, var(--font-ui))'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 2 }}>
            {STATUS_FILTERS.map(f => {
              const isActive = statusFilter === f.key
              return (
                <button
                  key={f.key}
                  className={`hmi-btn hmi-btn-sm ${isActive ? '' : 'hmi-btn-ghost'}`}
                  style={isActive
                    ? { background: 'var(--brand, var(--primary))', color: '#fff', fontWeight: 600, borderColor: 'transparent' }
                    : { fontWeight: 500 }
                  }
                  onClick={() => setStatusFilter(f.key)}
                >
                  {f.label}
                </button>
              )
            })}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)', marginRight: 4 }}>View:</span>
            {VIEW_MODES.map(v => {
              const isActive = viewMode === v.key
              return (
                <button
                  key={v.key}
                  className="hmi-btn hmi-btn-sm"
                  style={{
                    width: 30, height: 26, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? 'var(--ink)' : 'transparent',
                    color: isActive ? '#fff' : 'var(--ink-3)',
                    borderColor: isActive ? 'var(--ink)' : 'transparent'
                  }}
                  onClick={() => setViewMode(v.key)}
                  title={v.key}
                >
                  {v.icon}
                </button>
              )
            })}
          </div>
        </div>

        {/* Stat cards (continuous strip, no gap) + actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16 }}>
          <div className="stat-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, padding: 0 }}>
            <div style={{ padding: '14px 16px', borderRight: '1px solid var(--line-1, var(--line))' }}>
              <span className="eyebrow">Active Pipeline</span>
              <div className="num">{fmt(stats.activePipeline)}</div>
              <span className="sub">{stats.activeCount} projects in flight</span>
            </div>
            <div style={{ padding: '14px 16px', borderRight: '1px solid var(--line-1, var(--line))' }}>
              <span className="eyebrow">Out for Award</span>
              <div className="num">{fmt(stats.outForAward)}</div>
              <span className="sub">{stats.submittedCount} submitted</span>
            </div>
            <div style={{ padding: '14px 16px', borderRight: '1px solid var(--line-1, var(--line))' }}>
              <span className="eyebrow">Due This Week</span>
              <div className="num">{stats.dueThisWeek}</div>
              <span className="sub">Across {stats.activeCount} active</span>
            </div>
            <div style={{ padding: '14px 16px' }}>
              <span className="eyebrow">Recent Win Rate</span>
              <div className="num" style={{ color: stats.winRate >= 50 ? 'var(--ok)' : 'var(--risk)' }}>{stats.winRate}%</div>
              <span className="sub">{stats.wonCount}W · {stats.lostCount}L</span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
            <button className="hmi-btn hmi-btn-primary" onClick={() => setModalOpen(true)}>+ New project</button>
          </div>
        </div>

        {/* Count + sort indicator */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span>
            <b>{filtered.length}</b> of {projects.length} projects
            {statusFilter !== 'all' && <span> · filtered by <b>{STATUS_FILTERS.find(f => f.key === statusFilter)?.label}</b></span>}
          </span>
          <span style={{ color: 'var(--ink-3)' }}>Sorted by bid due · ascending</span>
        </div>

        {/* Content area — table / grid / board */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
        ) : viewMode === 'grid' ? (
          <GridView projects={filtered} onNavigate={(id) => history.push(`/bid-estimation/${id}`)} onNewProject={() => setModalOpen(true)} contextCounts={contextCounts} />
        ) : viewMode === 'board' ? (
          <BoardView projects={filtered} onNavigate={(id) => history.push(`/bid-estimation/${id}`)} contextCounts={contextCounts} />
        ) : (
          <div className="hmi-panel" style={{ overflow: 'visible' }}>
            <table className="hmi-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th style={{ width: 100 }}>Stage</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Bid Total</th>
                  <th style={{ width: 70, textAlign: 'right' }}>Margin</th>
                  <th style={{ width: 100 }}>Coverage</th>
                  <th style={{ width: 110 }}>Bid Due</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }}>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
                      No projects yet — click here to create one.
                    </td>
                  </tr>
                ) : (
                  filtered.map(project => {
                    const sp = STATUS_PILL[project.status] || STATUS_PILL.draft
                    const days = daysUntil(project.bidDueDate)
                    const margin = calcMargin(project)
                    const covPct = calcCovPct(project)

                    return (
                      <tr key={project._id} onClick={() => history.push(`/bid-estimation/${project._id}`)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{project.name}</span>
                              <span className={`pill ${sp.className}`}><span className="dot" />{sp.label}</span>
                              <ContextIndicator
                                hasBlocker={contextCounts[project._id]?.hasOutputBlocker}
                                noteCount={0}
                                hasReview={contextCounts[project._id]?.reviewsUpcoming > 0}
                                tasksOverdue={contextCounts[project._id]?.tasksOverdue}
                              />
                            </div>
                            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', fontFamily: 'var(--f-mono, var(--font-mono))' }}>
                              {[project.bidNumber, project.client, project.location].filter(Boolean).join(' \u00b7 ')}
                            </div>
                          </div>
                        </td>
                        <td>
                          <StageDots status={project.status} />
                          <span style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2, display: 'block' }}>{stageName(project.status)}</span>
                        </td>
                        <td className="num">{project.totalSell || project.totalCost ? fmt(project.totalSell || project.totalCost) : '\u2014'}</td>
                        <td className="num" style={{ color: marginColor(margin) }}>
                          {margin !== null ? margin.toFixed(1) + '%' : '\u2014'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontFamily: 'var(--f-mono, var(--font-mono))', fontSize: 12, color: 'var(--ink-3)' }}>{covPct}%</span>
                            <div className="cov"><div className="bar"><i style={{ width: `${covPct}%` }} /></div></div>
                          </div>
                        </td>
                        <td>
                          {project.bidDueDate ? (
                            <div>
                              <div style={{ fontSize: 12.5 }}>{new Date(project.bidDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                              {days !== null && (
                                <span className={`pill ${days <= 0 ? 'pill-risk' : days <= 7 ? 'pill-warn' : 'pill-neutral'}`} style={{ fontSize: 9, marginTop: 2 }}>
                                  <span className="dot" />{days <= 0 ? 'Overdue' : `${days}d left`}
                                </span>
                              )}
                            </div>
                          ) : '\u2014'}
                        </td>
                        <td>
                          <RowActionMenu project={project} onClone={handleClone} onDelete={setDeleteTarget} cloning={cloning} />
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <NewProjectModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} url={url} />
      <ScopeSelectionModal
        open={!!scopeModalProject}
        project={scopeModalProject}
        onComplete={(pid) => { setScopeModalProject(null); history.push(`/bid-estimation/${pid}`) }}
        onSkip={(pid) => { setScopeModalProject(null); if (pid) history.push(`/bid-estimation/${pid}`) }}
      />
      <Dialog
        open={!!deleteTarget}
        onClose={() => {
          if (deletingProject) return
          setDeleteTarget(null)
          setDeleteConfirmText('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            This permanently deletes <strong>{deleteTarget?.name}</strong>, its estimate records, bid packages,
            vendor quotes, parsed document pages, and related project files in Spaces. This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
            Type the project name to confirm.
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            autoFocus
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            disabled={deletingProject}
            placeholder={deleteTarget?.name || ''}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteTarget(null)
              setDeleteConfirmText('')
            }}
            disabled={deletingProject}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="secondary"
            variant="contained"
            disabled={!deleteTarget || deleteConfirmText !== deleteTarget.name || deletingProject}
            startIcon={deletingProject ? <CircularProgress size={16} /> : null}
          >
            {deletingProject ? 'Deleting...' : 'Delete Project and Files'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(BidEstimationProjectsDashboard)
